import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isValidEmail, subscribeToNewsletter } from '@/utils/newsletter';
import { trackEvent } from '@/utils/analytics';
import { usePersona } from '@/hooks/usePersona';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';

// T8 — map the picker's intent axis (QUAND) to the journey stage used by
// downstream copy + Beehiiv tags. exploring=dream, planning=research,
// move-soon=decided.
const INTENT_STAGE = { 'move-soon': 'decided', planning: 'research', exploring: 'dream' };

/**
 * Shared submission state machine for all four newsletter variants
 * (Banner / Inline / Sticky / ExitIntent). Lives outside the component
 * file so we can mock it in tests later.
 *
 * Returns:
 *   {
 *     email, setEmail,
 *     status: 'idle' | 'loading' | 'success' | 'already' | 'error',
 *     errorKey: 'errorEmail' | 'errorGeneric' | null,
 *     submit: (e?: FormEvent) => Promise<void>,
 *     reset: () => void,
 *   }
 *
 * @param {object} [opts]
 * @param {string} [opts.source='site']      Beehiiv UTM source.
 * @param {string} [opts.leadMagnet]         Optional lead-magnet identifier.
 *
 * @example
 *   const form = useNewsletterForm({ source: 'sticky' });
 *   <form onSubmit={form.submit}>
 *     <input value={form.email} onChange={(e) => form.setEmail(e.target.value)} />
 *     <button disabled={form.status === 'loading'}>Subscribe</button>
 *   </form>
 */
export function useNewsletterForm({ source = 'site', leadMagnet } = {}) {
  const { i18n } = useTranslation();
  const { personaSlug, intentSlug } = usePersona();
  const [email, setEmail] = useState('');
  // Honeypot field — a hidden input bots tend to fill, humans never see.
  // Named `website` (a classic bot magnet). On submit : if filled, we
  // fake success (don't tip the bot it failed), skip the API call entirely.
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorKey, setErrorKey] = useState(null);

  const submit = useCallback(
    async (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (status === 'loading') return;

      // Honeypot triggered → silent success. Bot believes it worked,
      // moves on, no spam in Beehiiv. We DO emit an internal event so
      // we can count spam attempts in GA4.
      if (honeypot.trim() !== '') {
        trackEvent('newsletter_spam_blocked', { source });
        setStatus('success');
        return;
      }

      if (!isValidEmail(email)) {
        setStatus('error');
        setErrorKey('errorEmail');
        return;
      }

      setStatus('loading');
      setErrorKey(null);

      const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;
      const outcome = await subscribeToNewsletter({
        email,
        source,
        leadMagnet,
        lang,
        persona: personaSlug || undefined,
        stage: INTENT_STAGE[intentSlug] || undefined,
      });

      if (outcome.status === 'success') {
        setStatus('success');
        trackEvent('newsletter_subscribe', { source, lead_magnet: leadMagnet });
      } else if (outcome.status === 'already') {
        setStatus('already');
      } else {
        setStatus('error');
        setErrorKey('errorGeneric');
      }
    },
    [email, honeypot, i18n.language, leadMagnet, source, status, personaSlug, intentSlug],
  );

  const reset = useCallback(() => {
    setEmail('');
    setHoneypot('');
    setStatus('idle');
    setErrorKey(null);
  }, []);

  return { email, setEmail, honeypot, setHoneypot, status, errorKey, submit, reset };
}
